import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Building2, MapPin, ArrowUpRight } from "lucide-react";

// v1.47 — magazine-style redesign
// - Full-bleed terracotta hero with editorial masthead
// - Warm earth palette (ochre / sand / clay / deep umber)
// - Big-scale hub cards: 4:5 image, gold rule accent, hover pan
export default function ArtCenters() {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/art-centers");
        setCenters(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="art-centers-page" className="bg-[#FAF6EE]">
      <Seo
        title="Art Hubs — African Art Collectives & Galleries"
        description="Discover African art hubs and collectives on AfricaArtMasters. Community studios and galleries presenting original works by their member artists."
        path="/art-hubs"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Art Hubs", path: "/art-hubs" },
        ])}
      />

      {/* --- Editorial hero ---------------------------------------- */}
      <section className="relative overflow-hidden bg-[#3A2418] text-[#F4EFE6]">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(circle at 20% 30%, #C97B3B 0%, transparent 55%), radial-gradient(circle at 85% 70%, #7A3D1F 0%, transparent 60%), linear-gradient(180deg, #3A2418 0%, #2A180F 100%)",
          }}
        />
        <div className="relative px-6 md:px-12 lg:px-24 py-24 md:py-36">
          <p className="label-eyebrow text-[#F4C430] tracking-[0.3em]">
            — The Collectives · Africa
          </p>
          <h1 className="font-serif-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] font-light mt-6 max-w-5xl">
            Where the <em className="text-[#F4C430]">masters</em> gather.
          </h1>
          <p className="mt-10 max-w-2xl text-lg md:text-xl leading-relaxed text-[#E8DFCA] font-light">
            Community studios, galleries and collectives across the continent —
            each a living archive of place and practice, presenting original
            works from their member artists direct from the studio.
          </p>
          {!loading && centers.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-8">
              <div>
                <p className="font-serif-display text-5xl text-[#F4C430]">
                  {centers.length}
                </p>
                <p className="label-eyebrow text-[#E8DFCA] mt-1">
                  Hub{centers.length === 1 ? "" : "s"} on record
                </p>
              </div>
              <div className="h-14 w-px bg-[#F4C430]/40" />
              <div>
                <p className="font-serif-display text-5xl text-[#F4C430]">
                  {centers.reduce((s, c) => s + (c.artwork_count || 0), 0)}
                </p>
                <p className="label-eyebrow text-[#E8DFCA] mt-1">Works on view</p>
              </div>
              <div className="h-14 w-px bg-[#F4C430]/40" />
              <div>
                <p className="font-serif-display text-5xl text-[#F4C430]">
                  {centers.reduce((s, c) => s + (c.member_count || 0), 0)}
                </p>
                <p className="label-eyebrow text-[#E8DFCA] mt-1">Member artists</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* --- Directory ---------------------------------------------- */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <p className="label-eyebrow text-[#B7502A]">— Index</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter font-light mt-2">
              Every hub, every story
            </h2>
          </div>
          <div className="h-px flex-1 bg-[#D8CBB2] mx-6 mb-3 hidden md:block" />
          <p className="font-mono-aam text-xs uppercase tracking-widest text-[#7A5C3D]">
            {centers.length} listed
          </p>
        </div>

        {loading ? (
          <p className="label-eyebrow text-[#7A5C3D]">Loading the hubs…</p>
        ) : centers.length === 0 ? (
          <p className="font-serif-display text-3xl text-[#7A5C3D]">
            No art hubs published yet — check back soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            {centers.map((c, i) => (
              <Link
                to={`/art-hub/${c.id}`}
                key={c.id}
                data-testid={`art-center-card-${c.id}`}
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#E8DFCA]">
                  {c.avatar_url ? (
                    <img
                      src={c.avatar_url}
                      alt={`${c.name}, African art hub${c.country ? ` in ${c.country}` : ""}`}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #C97B3B 0%, #7A3D1F 100%)",
                      }}
                    >
                      <Building2
                        className="w-20 h-20 text-[#F4EFE6]/60"
                        strokeWidth={1}
                      />
                    </div>
                  )}
                  {/* Gradient wash for text legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F]/85 via-[#2A180F]/10 to-transparent opacity-90 group-hover:opacity-95 transition-opacity" />
                  {/* Editorial index number */}
                  <p className="absolute top-4 left-5 font-mono-aam text-[10px] tracking-[0.3em] text-[#F4C430]">
                    №&nbsp;{String(i + 1).padStart(2, "0")}
                  </p>
                  {/* Corner arrow — appears on hover */}
                  <div className="absolute top-4 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <ArrowUpRight className="w-5 h-5 text-[#F4C430]" strokeWidth={1.5} />
                  </div>
                  {/* Overlay caption */}
                  <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                    {c.country && (
                      <p className="label-eyebrow text-[#F4C430] flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" strokeWidth={2} />
                        {c.country}
                      </p>
                    )}
                    <h3 className="font-serif-display text-3xl md:text-4xl tracking-tight leading-none text-[#F4EFE6] mt-2">
                      {c.name}
                    </h3>
                    <div className="mt-4 h-px w-10 bg-[#F4C430] transition-all duration-500 group-hover:w-24" />
                    <p className="mt-4 text-sm text-[#E8DFCA] font-light">
                      {c.member_count} artist{c.member_count === 1 ? "" : "s"}
                      <span className="mx-2 text-[#F4C430]">·</span>
                      {c.artwork_count} work{c.artwork_count === 1 ? "" : "s"} on view
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
